import cx from 'clsx';
import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';

import { ReactComponent as ArrowIcon } from '@assets/svg/ic-aiNavArrow.svg';

import useTranslation from '@hooks/useTranslation';

import { selectActionItemsSearch } from '@pages/ActionItems/store/useActionItemsStore';
import {
  selectAIStatus,
  selectAIType,
  selectAIUrgency,
} from '@pages/ActionItems/store/useActionItemsStorePersist';
import type { IArchivedAiData, IArchivedAiStore } from '@pages/Archive/model/archivedAi';
import { useArchivedAiListInfiniteQuery } from '@pages/Archive/services/useArchivedAiListInfiniteQuery';
import { selectArchiveFiltersData } from '@pages/Archive/store/useArchiveFiltersStore';

import { useActionItemsGlobalOrIdListQuery } from '@services/actionItem/useActionItemsGlobalOrIdListQuery';

import { selectAppLocationSelectValue } from '@store/useAppStore';

import style from './style.module.scss';

interface IProps {
  isArchivedAi: boolean;
}

const Navigation: React.FC<IProps> = ({ isArchivedAi }) => {
  const { t } = useTranslation();
  const { customerId, actionItemId } = useParams();

  const locationSelectValue = selectAppLocationSelectValue();
  const typeStore = selectAIType();
  const urgencyType = selectAIUrgency();
  const statusType = selectAIStatus();
  const aiListSearch = selectActionItemsSearch();
  const {
    causeCategory,
    timeUpdatedRange,
    timeCreatedReportRange,
    costAvoidedRange,
    searchValue,
    type,
    sorting,
  }: IArchivedAiStore = selectArchiveFiltersData();

  const { data: actionItemsData } = useActionItemsGlobalOrIdListQuery({
    filter: {
      urgency: urgencyType.value,
      type: typeStore.value,
      state: statusType.value,
    },
    locationIds:
      locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue.filterIds,
    search: aiListSearch,
    enabled: !isArchivedAi,
  });

  const {
    data: archiveActionItemsData,
    fetchNextPage,
    hasNextPage,
    isFetching: isArchiveFetching,
  } = useArchivedAiListInfiniteQuery({
    sorting,
    locationIds:
      locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue.filterIds,
    searchValue: searchValue,
    causeCategory,
    timeUpdatedRange,
    timeCreatedReportRange,
    costAvoidedRange: costAvoidedRange,
    type,
    enabled: isArchivedAi,
  });

  // @ts-ignore TODO: update react-query to v5
  const archiveAisData: IArchivedAiData = archiveActionItemsData;

  const aisListData = !isArchivedAi ? actionItemsData : archiveAisData;
  const neighborAis = actionItemId && aisListData?.neighborAisById[actionItemId];

  useEffect(() => {
    if (isArchivedAi && neighborAis && !neighborAis?.nextId && hasNextPage && !isArchiveFetching) {
      fetchNextPage();
    }
  }, [isArchivedAi, neighborAis, hasNextPage, isArchiveFetching]);

  if (!neighborAis) {
    return null;
  }

  return (
    <>
      <div className={style.navigationBlock}>
        <div className={style.navigationCounter}>
          {neighborAis?.currentPosition} {t('of')} {aisListData.totalCount}
        </div>
        <Link
          to={neighborAis.prevId ? `/customer/${customerId}/ai/${neighborAis.prevId}` : ''}
          className={cx(style.navigatePrev, { [style.disabledLink]: !neighborAis.prevId })}
        >
          <ArrowIcon />
        </Link>
        <Link
          to={neighborAis.nextId ? `/customer/${customerId}/ai/${neighborAis.nextId}` : ''}
          className={cx(style.navigateNext, { [style.disabledLink]: !neighborAis.nextId })}
        >
          <ArrowIcon />
        </Link>
      </div>
      <span className={style.separator} />
    </>
  );
};

export default Navigation;
